/**
 * CatalogueEditor (Phase 44.5).
 *
 * The largest single editor in the setup flow. 3-pillar + initiatives
 * nested structure, drag-to-reorder capabilities, collapsible cards,
 * validation sidebar that tracks TODOs / unresolved initiative refs in
 * real time.
 *
 * Autosave strategy: every edit produces a FULL updated catalogue (not
 * field-level patches) via `updateCatalogue({pillars})` or
 * `updateCatalogue({strategic_initiatives})`. The store's deep-merge
 * treats arrays as replacements, so this is just easier than
 * reconstructing a path-based JSON patch for a nested tree where
 * positions matter. Debounce is 1s (longer than Profile's 500ms
 * because payloads are larger).
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { useSetupStore } from "../../stores/setupStore.js";
import CapabilityCard from "./components/CapabilityCard.jsx";
import InitiativeCard from "./components/InitiativeCard.jsx";
import ValidationSidebar from "./components/ValidationSidebar.jsx";
import ResearchPanel from "./components/ResearchPanel.jsx";

// A frontend-only stable identifier for sortable/react keys. Stripped
// before PUT (see `stripUids` below) so the backend validator never
// sees the extra `_uid` attribute (which would fail the schema's
// additionalProperties: false).
function newUid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `cap-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function ensureUid(cap) {
  if (cap && typeof cap === "object" && !cap._uid) {
    return { ...cap, _uid: newUid() };
  }
  return cap;
}

function stripUid(cap) {
  if (!cap || typeof cap !== "object") return cap;
  // eslint-disable-next-line no-unused-vars
  const { _uid, ...rest } = cap;
  return rest;
}

function emptyCapability() {
  return {
    _uid: newUid(),
    name: "New capability",
    what_it_does: "TODO",
    problems_solved: [],
    effort: "TODO",
    value_return: "TODO",
  };
}

// Minimal pillar scaffold for the zero-pillar empty state. Slug must
// pass the validator's `^[a-z][a-z0-9_]{0,49}$` pattern.
function emptyPillar(index) {
  return {
    id: `pillar_${index + 1}`,
    label: `Pillar ${index + 1}`,
    description: "",
    capabilities: [],
  };
}

function emptyInitiative() {
  return { name: "New initiative", description: "", capabilities_required: [] };
}

export default function CatalogueEditor() {
  const catalogue = useSetupStore((s) => s.catalogue);
  const updateCatalogue = useSetupStore((s) => s.updateCatalogue);
  const validationErrors = useSetupStore((s) => s.validationErrors.catalogue);

  // Tag each capability with a stable `_uid` so sortable keys + React
  // keys survive index shifts (remove/reorder). Stripped at dispatch
  // time by `pushPillars` so the backend schema never sees the extra
  // attribute.
  const pillars = useMemo(() => {
    const raw = catalogue?.pillars || [];
    return raw.map((p) => ({
      ...p,
      capabilities: (p?.capabilities || []).map(ensureUid),
    }));
  }, [catalogue]);
  const initiatives = useMemo(
    () => catalogue?.strategic_initiatives || [],
    [catalogue]
  );

  const pushPillars = (nextPillars) => {
    const clean = nextPillars.map((p) => ({
      ...p,
      capabilities: (p?.capabilities || []).map(stripUid),
    }));
    updateCatalogue({ pillars: clean });
  };

  // activeTab: 0..2 for pillars, "initiatives" for the strategic list.
  const [activeTab, setActiveTab] = useState(0);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Flat list of capability names for InitiativeCard's ref picker.
  const allCapabilityNames = useMemo(() => {
    const names = [];
    for (const p of pillars) {
      for (const c of p.capabilities || []) {
        if (c?.name?.trim()) names.push(c.name.trim());
      }
    }
    return Array.from(new Set(names));
  }, [pillars]);

  if (!catalogue) {
    return (
      <div className="setup-section setup-section--with-side">
        <div>
          <h2>Describe your product catalogue</h2>
          <p className="setup-section__empty">
            No catalogue drafted yet. The bootstrap pipeline will populate this
            once the profile is in place, or run Regenerate from the research
            panel.
          </p>
        </div>
        {/* The ResearchPanel hosts the Regenerate button — without it the
            empty-state copy was telling the user to click a button that
            wasn't on the page. Surfaced for tenant_anthropic during v4.3
            validation: profile populated via regen but the catalogue tab
            was a dead end. */}
        <ResearchPanel section="catalogue" metadata={null} />
      </div>
    );
  }

  // ── Mutation helpers ───────────────────────────────────────────────────────
  const replacePillar = (index, patch) => {
    const next = pillars.slice();
    next[index] = { ...next[index], ...patch };
    pushPillars(next);
  };

  const replaceCapability = (pIdx, cIdx, cap) => {
    const next = pillars.slice();
    const caps = (next[pIdx].capabilities || []).slice();
    caps[cIdx] = cap;
    next[pIdx] = { ...next[pIdx], capabilities: caps };
    pushPillars(next);
  };

  const addCapability = (pIdx) => {
    const next = pillars.slice();
    const caps = (next[pIdx].capabilities || []).slice();
    caps.push(emptyCapability());
    next[pIdx] = { ...next[pIdx], capabilities: caps };
    pushPillars(next);
  };

  const addPillar = () => {
    const next = [...pillars, emptyPillar(pillars.length)];
    pushPillars(next);
    setActiveTab(next.length - 1);
  };

  const removeCapability = (pIdx, cIdx) => {
    const next = pillars.slice();
    const caps = (next[pIdx].capabilities || []).slice();
    caps.splice(cIdx, 1);
    next[pIdx] = { ...next[pIdx], capabilities: caps };
    pushPillars(next);
  };

  const reorderCapability = (pIdx, fromIdx, toIdx) => {
    const next = pillars.slice();
    const caps = (next[pIdx].capabilities || []).slice();
    next[pIdx] = { ...next[pIdx], capabilities: arrayMove(caps, fromIdx, toIdx) };
    pushPillars(next);
  };

  const handleDragEnd = (pIdx) => (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const caps = pillars[pIdx].capabilities || [];
    const fromIdx = caps.findIndex((c) => c._uid === active.id);
    const toIdx = caps.findIndex((c) => c._uid === over.id);
    if (fromIdx !== -1 && toIdx !== -1 && fromIdx !== toIdx) {
      reorderCapability(pIdx, fromIdx, toIdx);
    }
  };

  const navigateToIssue = (target) => {
    if (!target) return;
    if (target.tab === "initiatives") {
      setActiveTab("initiatives");
    } else if (typeof target.pillar === "number") {
      setActiveTab(target.pillar);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  // Defensive: if the caller lands on pillar-index 2 but the catalogue
  // only has 1 pillar (regen dropped some), snap to a valid tab rather
  // than rendering undefined.
  const effectiveTab =
    typeof activeTab === "number" && activeTab >= pillars.length
      ? pillars.length > 0
        ? 0
        : "initiatives"
      : activeTab;
  const currentPillar = typeof effectiveTab === "number" ? pillars[effectiveTab] : null;

  return (
    <div className="setup-section setup-section--split">
      <div className="setup-section__main">
        <div className="setup-section__header">
          <h2>Describe your product catalogue</h2>
          <p className="setup-section__muted">
            Step 2 of 3. Three pillars, capabilities under each, plus any
            strategic initiatives that cut across them. Edits save
            automatically.
          </p>
        </div>

        {validationErrors?.errors?.length > 0 && (
          <div className="validation-errors" role="alert">
            <strong>Validation errors — latest save was rejected:</strong>
            <ul>
              {validationErrors.errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Tab strip */}
        <div className="catalogue-tabs" role="tablist">
          {pillars.map((pillar, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={effectiveTab === i}
              className={`catalogue-tabs__tab ${
                effectiveTab === i ? "catalogue-tabs__tab--active" : ""
              }`}
              onClick={() => setActiveTab(i)}
            >
              {pillar?.label || `Pillar ${i + 1}`}
              <span className="catalogue-tabs__count">
                {(pillar?.capabilities || []).length}
              </span>
            </button>
          ))}
          <button
            type="button"
            role="tab"
            aria-selected={effectiveTab === "initiatives"}
            className={`catalogue-tabs__tab ${
              effectiveTab === "initiatives" ? "catalogue-tabs__tab--active" : ""
            }`}
            onClick={() => setActiveTab("initiatives")}
          >
            Initiatives
            <span className="catalogue-tabs__count">{initiatives.length}</span>
          </button>
          {pillars.length < 3 && (
            <button
              type="button"
              className="catalogue-tabs__add"
              onClick={addPillar}
              title="Add pillar"
            >
              + Add pillar
            </button>
          )}
        </div>

        {/* Zero-pillar empty state — the validator requires exactly 3,
            so surface it prominently until the admin hits the cap. */}
        {pillars.length === 0 && effectiveTab !== "initiatives" && (
          <div className="setup-section__empty">
            <p>
              <strong>No pillars yet.</strong> The ProductCatalogue validator
              requires exactly 3 pillars before you can activate. Add one to
              get started, or regenerate the catalogue from research.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={addPillar}
            >
              + Add first pillar
            </button>
          </div>
        )}

        {/* Pillar view */}
        {currentPillar && (
          <div className="catalogue-pillar">
            <label className="setup-field">
              <span className="setup-field__label">Pillar label</span>
              <input
                type="text"
                className="setup-field__input"
                value={currentPillar.label || ""}
                onChange={(e) => replacePillar(effectiveTab, { label: e.target.value })}
                maxLength={100}
              />
            </label>
            <label className="setup-field">
              <span className="setup-field__label">Description</span>
              <textarea
                className="setup-field__input"
                rows={3}
                value={currentPillar.description || ""}
                onChange={(e) =>
                  replacePillar(effectiveTab, { description: e.target.value })
                }
                maxLength={1000}
              />
            </label>

            <h3 className="catalogue-pillar__caps-heading">Capabilities</h3>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd(effectiveTab)}
            >
              <SortableContext
                items={(currentPillar.capabilities || []).map((c) => c._uid)}
                strategy={verticalListSortingStrategy}
              >
                {(currentPillar.capabilities || []).map((cap, cIdx) => (
                  <CapabilityCard
                    key={cap._uid}
                    id={cap._uid}
                    capability={cap}
                    onChange={(next) => replaceCapability(effectiveTab, cIdx, next)}
                    onRemove={() => removeCapability(effectiveTab, cIdx)}
                  />
                ))}
              </SortableContext>
            </DndContext>
            <button
              type="button"
              className="btn btn-secondary catalogue-pillar__add"
              onClick={() => addCapability(effectiveTab)}
            >
              + Add capability
            </button>
          </div>
        )}

        {/* Initiatives view */}
        {effectiveTab === "initiatives" && (
          <div className="catalogue-initiatives">
            <h3 className="catalogue-pillar__caps-heading">Strategic initiatives</h3>
            <p className="setup-field__help">
              Programmes that cut across pillars — they reference specific
              capabilities. At least one is required before activation.
            </p>
            {initiatives.length === 0 && (
              <p className="initiative-card__empty">
                No strategic initiatives yet.
              </p>
            )}
            {initiatives.map((init, iIdx) => (
              <InitiativeCard
                key={iIdx}
                initiative={init}
                capabilityNames={allCapabilityNames}
                onChange={(next) => {
                  const list = initiatives.slice();
                  list[iIdx] = next;
                  updateCatalogue({ strategic_initiatives: list });
                }}
                onRemove={() => {
                  const list = initiatives.slice();
                  list.splice(iIdx, 1);
                  updateCatalogue({ strategic_initiatives: list });
                }}
              />
            ))}
            <button
              type="button"
              className="btn btn-secondary catalogue-pillar__add"
              onClick={() =>
                updateCatalogue({
                  strategic_initiatives: [...initiatives, emptyInitiative()],
                })
              }
            >
              + Add initiative
            </button>
          </div>
        )}

        <div className="setup-footer">
          <Link to="/setup/profile" className="btn btn-ghost">
            Back to profile
          </Link>
          <Link to="/setup/activate" className="btn btn-primary">
            Save &amp; continue
          </Link>
        </div>
      </div>

      <div className="setup-section__side">
        <ValidationSidebar
          catalogue={catalogue}
          activeTab={effectiveTab}
          onNavigate={navigateToIssue}
        />
        <ResearchPanel
          section="catalogue"
          metadata={catalogue.catalogue_metadata}
        />
      </div>
    </div>
  );
}
